import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import {
  ShoppingCart, CheckCircle, XCircle, PauseCircle, Truck, Edit, Circle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const ICON_MAP = {
  "shopping-cart": ShoppingCart,
  "check-circle": CheckCircle,
  "x-circle": XCircle,
  "pause-circle": PauseCircle,
  "truck": Truck,
  "edit": Edit,
  "circle": Circle,
};

/**
 * OrderTimeline dialog: pulls /api/orders/{order_id}/timeline and renders a vertical timeline.
 * Props: orderId (string | null), onClose().
 */
const OrderTimeline = ({ orderId, onClose }) => {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${API}/orders/${orderId}/timeline`);
        const data = await res.json();
        if (data.success) {
          setEvents(data.data.timeline || []);
          setStatus(data.data.current_status || "");
        }
      } finally { setLoading(false); }
    })();
  }, [orderId]);

  return (
    <Dialog open={!!orderId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="order-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#8B1A1A]" /> Order Timeline — {orderId}
            {status && <Badge variant="outline" className="ml-2 text-[10px]">{status}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-12 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>
        ) : events.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">No timeline events yet</p>
        ) : (
          <div className="relative pl-8 py-4">
            <div className="absolute left-3 top-4 bottom-4 w-0.5 bg-slate-200" />
            {events.map((ev, i) => {
              const Icon = ICON_MAP[ev.icon] || Circle;
              return (
                <div key={i} className="relative mb-5" data-testid={`timeline-event-${i}`}>
                  <div className="absolute -left-8 top-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow"
                    style={{ backgroundColor: ev.color || "#6B7280" }}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">{ev.label}</p>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {ev.timestamp ? new Date(ev.timestamp).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                      </span>
                    </div>
                    {ev.by && <p className="text-xs text-slate-500 mt-0.5">By <strong>{ev.by}</strong></p>}
                    {ev.details && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{ev.details}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderTimeline;
